import { Button, Drawer, Flex, Form, List, message, Spin } from 'antd';
import ActionHeader from '../../../../components/actionHeader';
import { StyledListItem } from '../../../../styles/listItem';
import { ListContainer } from './styles';
import { deleteRoutine } from '../../../../utils/httpRequest';
import RoutineForm from '../../../../components/routineForm';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import RoutineCard from '../../../../components/routineCard';
import Tab from '../../../../components/tab';
import {
  selectTemplateCurrentCauses,
  selectTemplateRoutines,
  selectTemplateCurrentRoutines,
  selectTemplateToBeReviewedRoutines,
  selectTemplateRejectedRoutines,
  selectIsFetchingTemplates,
  fetchTemplateRoutines,
  fetchTemplateCauses,
} from '../../../../slices/templates';
import { useAppDispatch } from '../../../../store';
import { Routine } from '../../../../types';

const TemplateRoutines = () => {
  const { id = '' } = useParams();
  const dispatch = useAppDispatch();
  const [openForm, setOpenForm] = useState(false);
  const [routineId, setRoutineId] = useState('');
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const causes = useSelector(selectTemplateCurrentCauses);
  const routines = useSelector(selectTemplateRoutines);
  const currentRoutines = useSelector(selectTemplateCurrentRoutines);
  const toBeReviewedRoutines = useSelector(selectTemplateToBeReviewedRoutines);
  const rejectedRoutines = useSelector(selectTemplateRejectedRoutines);

  const isFetching = useSelector(selectIsFetchingTemplates);

  const routineItem = routines?.find((i) => i.routineID === routineId);

  const handleSearch = (searchText?: string) => {
    if (!isFetching) {
      dispatch(
        fetchTemplateRoutines({
          templateId: id,
          params: { search: searchText },
        })
      );
    }
  };

  const handleAddClick = () => {
    form.resetFields();
    setRoutineId('');
    setOpenForm(true);
  };

  const handleEdit = (routineID: string) => {
    const item = routines?.find((i) => i.routineID === routineID);
    form.setFieldsValue(item);
    setOpenForm(true);
    setRoutineId(routineID);
  };

  const handleDelete = async (routineID: string) => {
    try {
      setLoading(true);
      await deleteRoutine(routineID);
      messageApi.success('Delete Successfully!');
      dispatch(fetchTemplateRoutines({ templateId: id }));
    } catch (error) {
      console.log(error);
      messageApi.success('Delete Failed! Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (routine: Routine) => {
    setOpenForm(false);
    dispatch(fetchTemplateRoutines({ templateId: id }));
  };

  useEffect(() => {
    if (!routines?.length) {
      dispatch(fetchTemplateRoutines({ templateId: id }));
    }
    if (!causes?.length) {
      dispatch(fetchTemplateCauses({ templateId: id }));
    }
  }, []);

  const tabs = [
    {
      id: 1,
      title: `Current (${currentRoutines.length})`,
      component: (
        <ListContainer>
          <List
            dataSource={currentRoutines}
            loading={isFetching}
            renderItem={(item) => (
              <StyledListItem>
                <RoutineCard
                  routine={item}
                  showEdit
                  onEdit={handleEdit}
                  showDelete
                  onDelete={handleDelete}
                  showSuggestionStatus
                />
              </StyledListItem>
            )}
          ></List>
        </ListContainer>
      ),
    },
    {
      id: 2,
      title: `To-Be-Reviewed (${toBeReviewedRoutines.length})`,
      component: (
        <ListContainer>
          <List
            dataSource={toBeReviewedRoutines}
            loading={isFetching}
            renderItem={(item) => (
              <StyledListItem>
                <RoutineCard
                  routine={item}
                  showEdit
                  onEdit={handleEdit}
                  showDelete
                  onDelete={handleDelete}
                  showSuggestionStatus
                />
              </StyledListItem>
            )}
          ></List>
        </ListContainer>
      ),
    },
    {
      id: 3,
      title: `Rejected (${rejectedRoutines.length})`,
      component: (
        <ListContainer>
          <List
            dataSource={rejectedRoutines}
            loading={isFetching}
            renderItem={(item) => (
              <StyledListItem>
                <RoutineCard
                  routine={item}
                  onEdit={handleEdit}
                  showDelete
                  onDelete={handleDelete}
                  showSuggestionStatus
                />
              </StyledListItem>
            )}
          ></List>
        </ListContainer>
      ),
    },
  ];

  return (
    <Flex vertical flex={1} gap={8} style={{ height: '100%' }}>
      <ActionHeader
        title="Routine"
        isSecondaryTitle
        showBorder={false}
        showSearch
        onSearch={handleSearch}
        endSlot={
          <>
            <Button type="primary" disabled ghost onClick={handleAddClick}>
              Batch Upload
            </Button>
            <Button type="primary" onClick={handleAddClick}>
              Add New Routine
            </Button>
          </>
        }
      />
      {contextHolder}
      <Flex flex={1} style={{ overflowY: 'auto' }}>
        <Tab tabs={tabs} />
      </Flex>
      <Spin spinning={loading} fullscreen />
      <Drawer
        title={routineId ? `Edit ${routineItem?.name}` : 'Add New Routine'}
        open={openForm}
        footer={null}
        width={800}
        onClose={() => {
          setOpenForm(false);
          setRoutineId('');
        }}
      >
        <Form form={form} layout="vertical">
          <RoutineForm
            form={form}
            onCancel={() => {
              setOpenForm(false);
              setRoutineId('');
            }}
            onSubmit={handleSubmit}
            initialRoutine={routineItem}
          />
        </Form>
      </Drawer>
    </Flex>
  );
};

export default TemplateRoutines;
